import React from "react";
import { Handshake } from "lucide-react";
import MarkThumbnail from "./MarkThumbnail";
import GoodsAndServices from "./GoodsAndServices";
import { StatusBadge } from "../ui/Badge";
import {
  EM_DASH,
  SIMILARITY_TEXT_CLASS,
  formatOwners,
  similarityTone,
} from "../../lib/format";

/**
 * Grid view: one card per mark, picture on top, the facts underneath.
 *
 * Built for comparing logos side by side — a 48px table thumbnail is too
 * small to judge visual similarity — while still carrying the fields that
 * decide whether a mark matters: goods & services, class, serial and owner.
 */
function Row({ label, children }) {
  return (
    <>
      <dt className="text-sm font-semibold text-ink-900">{label}</dt>
      <dd className="min-w-0 text-sm text-ink-700">{children}</dd>
    </>
  );
}

export default function ResultsGallery({
  rows,
  selected,
  onToggleRow,
  similarityMap,
  onOpenImage,
  onOpenDetail,
  onFindLicensing,
  isDetailLoading = () => false,
}) {
  return (
    <div className="grid grid-cols-1 gap-4 border-x border-ink-200 bg-white p-4 md:grid-cols-2 2xl:grid-cols-3">
      {rows.map((row, index) => {
        const isSelected = selected.has(row.trademarkId);
        const score = similarityMap.get(row.trademarkId);
        const owners = formatOwners(row);

        return (
          <article
            key={row.trademarkId || index}
            onClick={() => onOpenDetail?.(row)}
            tabIndex={onOpenDetail ? 0 : undefined}
            role={onOpenDetail ? "button" : undefined}
            onKeyDown={(e) => {
              if (
                onOpenDetail &&
                e.target === e.currentTarget &&
                (e.key === "Enter" || e.key === " ")
              ) {
                e.preventDefault();
                onOpenDetail(row);
              }
            }}
            className={`flex flex-col rounded border p-4 transition-shadow ${
              onOpenDetail ? "cursor-pointer hover:shadow-float" : ""
            } ${isSelected ? "border-[#8f5eeb]-400 bg-[#8f5eeb]-100/50" : "border-ink-200 bg-white"}`}
          >
            <div className="flex items-center justify-between">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggleRow(row.trademarkId)}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Select ${row.wordMark || "this record"}`}
                className="h-4 w-4 cursor-pointer rounded border-ink-300 text-ink-900 focus:ring-seal-500/40"
              />
              <div className="flex items-center gap-2">
                {score !== undefined && (
                  <span
                    className={`text-xs font-bold ${SIMILARITY_TEXT_CLASS[similarityTone(score)]}`}
                  >
                    {score}% similar
                  </span>
                )}
                {onFindLicensing && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onFindLicensing(row);
                    }}
                    title="Find potential licensing"
                    aria-label={`Find potential licensing for ${row.wordMark || "this mark"}`}
                    className="rounded p-1.5 text-ink-400 hover:bg-[#8f5eeb]-100 hover:text-[#8f5eeb]-600"
                  >
                    <Handshake className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>

            <div className="mt-2 flex" onClick={(e) => e.stopPropagation()}>
              <MarkThumbnail
                wordMark={row.wordMark}
                imageUrl={row.imageUrl}
                size="card"
                onClick={() =>
                  onOpenImage({ url: row.imageUrl, wordMark: row.wordMark })
                }
              />
            </div>

            <dl className="mt-4 grid grid-cols-[92px_minmax(0,1fr)] gap-x-3 gap-y-2.5">
              <Row label="Wordmark">
                <span className="break-words font-semibold text-[#8f5eeb]-600 underline underline-offset-2">
                  {row.wordMark || EM_DASH}
                </span>
              </Row>
              <Row label="Status">
                <StatusBadge status={row.status} />
              </Row>
              <Row label="Goods & services">
                <GoodsAndServices
                  items={row.goodsAndServices}
                  loading={isDetailLoading(row)}
                  lines={3}
                  expandable
                />
              </Row>
              <Row label="Class">
                {row.niceClasses?.length ? row.niceClasses.join(", ") : EM_DASH}
              </Row>
              <Row label="Serial">
                <span className="font-mono text-xs">
                  {row.applicationNumber || EM_DASH}
                </span>
              </Row>
              <Row label="Owners">
                {owners ? (
                  <span className="line-clamp-2 break-words" title={owners}>
                    {owners}
                  </span>
                ) : (
                  <span className="text-ink-300">
                    {isDetailLoading(row) ? "Loading…" : "Not published"}
                  </span>
                )}
              </Row>
            </dl>
          </article>
        );
      })}
    </div>
  );
}
